import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import {
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    LinearProgress
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { get, put, postFiles } from "../../config/axios";
import toast from "react-hot-toast";

const HomeCarousel = () => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({
        heading: "",
        subheading: "",
        description: "",
        image: "",
        video: ""
    });
    const [carouselData, setCarouselData] = useState(null);

    const getCarouselData = async () => {
        try {
            setLoading(true);
            const response = await get(`/api/dashboard/website/home`);
            const carousel = response.find(item => item.section === "carousel");
            setCarouselData(carousel);
        } catch (error) {
            console.error("Error fetching carousel data:", error);
            toast.error("Failed to fetch carousel data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCarouselData();
    }, []);

    const handleOpenModal = (index) => {
        setCurrentSlideIndex(index);
        setFormData(carouselData.content[index]);
        setVideoFile(null);
        setImageFile(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setCurrentSlideIndex(null);
        setVideoFile(null);
        setImageFile(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleVideoSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                toast.error("Video file size should be less than 50MB");
                return;
            }
            setVideoFile(file);
        }
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image file size should be less than 5MB");
                return;
            }
            setImageFile(file);
        }
    };

    const handleSubmit = async () => {
        try {
            setUploading(true);
            let imageUrl = formData.image;
            let videoUrl = formData.video;

            // Upload image if new file selected
            if (imageFile) {
                const imageFormData = new FormData();
                imageFormData.append("file", imageFile);
                const { data: { url } } = await postFiles("/api/app/user/uploadImage", imageFormData);
                imageUrl = url;
            }

            // Upload video if new file selected
            if (videoFile) {
                const videoFormData = new FormData();
                videoFormData.append("file", videoFile);
                const { data: { url } } = await postFiles("/api/app/user/uploadImage", videoFormData);
                videoUrl = url;
            }

            const updatedSlide = {
                ...formData,
                image: imageUrl,
                video: videoUrl
            };

            // Update the carousel content
            const updatedContent = [...carouselData.content];
            updatedContent[currentSlideIndex] = updatedSlide;

            // Send to API
            await put(`/api/dashboard/website/home/${carouselData._id}`, {
                content: updatedContent
            });

            toast.success("Slide updated successfully");
            await getCarouselData();
            handleCloseModal();
        } catch (error) {
            console.error("Error updating slide:", error);
            toast.error("Failed to update slide");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Layout>
            <div style={{ padding: "1rem" }}>
                <Typography variant="h5" paddingY={2}>
                    Manage Home Carousel
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : carouselData ? (
                    <>
                        <TableContainer component={Paper} sx={{ maxWidth: "100%", maxHeight: 400, overflow: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Slide #</b></TableCell>
                                        <TableCell><b>Heading</b></TableCell>
                                        <TableCell><b>Image Preview</b></TableCell>
                                        <TableCell><b>Video Preview</b></TableCell>
                                        <TableCell><b>Action</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carouselData.content.map((slide, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{slide.heading || "Untitled"}</TableCell>
                                            <TableCell>
                                                {slide.image ? (
                                                    <img
                                                        src={slide.image}
                                                        alt={`Slide ${index + 1}`}
                                                        style={{ width: 100, height: 60, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => window.open(slide.image, '_blank')}
                                                    />
                                                ) : (
                                                    "No Image"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {slide.video ? (
                                                    <video width="150" controls>
                                                        <source src={slide.video} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                ) : (
                                                    "No Video"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleOpenModal(index)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Edit Slide Modal */}
                        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                            <DialogTitle>
                                Edit Slide {currentSlideIndex !== null ? currentSlideIndex + 1 : ''}
                            </DialogTitle>
                            <DialogContent>
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Heading"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleInputChange}
                                        sx={{ mb: 3 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Subheading"
                                        name="subheading"
                                        value={formData.subheading}
                                        onChange={handleInputChange}
                                        sx={{ mb: 3 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={4}
                                        sx={{ mb: 3 }}
                                    />

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Image
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            sx={{ mr: 2 }}
                                        >
                                            {imageFile ? "Change Image" : "Select Image"}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={handleImageSelect}
                                            />
                                        </Button>
                                        {imageFile && (
                                            <Typography variant="body2">
                                                {imageFile.name} ({(imageFile.size / (1024 * 1024)).toFixed(2)} MB)
                                            </Typography>
                                        )}
                                        {formData.image && !imageFile && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Current image will be kept
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Video
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            sx={{ mr: 2 }}
                                        >
                                            {videoFile ? "Change Video" : "Select Video"}
                                            <input
                                                type="file"
                                                hidden
                                                accept="video/*"
                                                onChange={handleVideoSelect}
                                            />
                                        </Button>
                                        {videoFile && (
                                            <Typography variant="body2">
                                                {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                                            </Typography>
                                        )}
                                        {formData.video && !videoFile && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Current video will be kept
                                            </Typography>
                                        )}
                                    </Box>

                                    {uploading && <LinearProgress sx={{ mt: 2 }} />}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseModal} disabled={uploading}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={uploading}
                                    variant="contained"
                                >
                                    {uploading ? "Updating..." : "Update Slide"}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                ) : (
                    <Typography>No carousel data found</Typography>
                )}
            </div>
        </Layout>
    );
};

export default HomeCarousel;